'use client'
import * as React from 'react'
import styles from './styles.module.css'
import Image from 'next/image'
import Tag from '../tag'
import Link from 'next/link'
import {useSearchParams} from 'next/navigation'
import HighlightBlog from '../highlight-blog/HighlightBlog'
import BlogFilter from '../blog-filter/BlogFilter'
import BlogHeader from '../blog-header/BlogHeader'
import {getWordPressBlogs, getWordPressCategories, getWordPressSearchedBlogs} from './wordpress'
import {format} from 'date-fns'
import parse from 'html-react-parser'
import readTimeEstimate from 'read-time-estimate'
import clsx from 'clsx'

const Blogs = () => {
  const [blogsData, setBlogsData] = React.useState<
    | {
        blogs: any
        page_number: number
        total_blog: any
        total_page: any
      }
    | undefined
  >({blogs: [], page_number: 1, total_blog: '', total_page: ''})
  const [searchedBlogs, setSearchedBlogs] = React.useState<
    | {
        blogs: any
        page_number: number
        total_blog: any
        total_page: any
      }
    | undefined
  >({blogs: [], page_number: 1, total_blog: '', total_page: ''})
  const [activePage, setActivePage] = React.useState(1)
  const [recommendedBlog, setRecommendedBlog] = React.useState<any>()
  const [categories, setCategories] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const [currentBlogs, setCurrentBlogs] = React.useState<any[]>([])
  const [searchText, setSearchText] = React.useState('')
  const params = useSearchParams()

  const getBlogReadingTime = (string: string) => {
    const {humanizedDuration} = readTimeEstimate(string, 275, 12, 500, ['img', 'Image'])
    return humanizedDuration
  }

  const getBlogs = async (page_number = 1) => {
    setActivePage(page_number)
    let categoryId = params.get('tag')
    setLoading(true)
    let data = await getWordPressBlogs(page_number, categoryId ? `&categories=${categoryId}` : '')
    if (!recommendedBlog) {
      setRecommendedBlog(data?.blogs[0])
    }
    setBlogsData(data)
    setCurrentBlogs(data?.blogs)
    setLoading(false)
  }

  const getBlogsBySearch = async (searchText: string, page_number = 1) => {
    setActivePage(page_number)
    if (searchText) {
      setLoading(true)
      const data = await getWordPressSearchedBlogs(searchText, page_number)
      setSearchText(searchText)
      setSearchedBlogs(data)
      setCurrentBlogs(data?.blogs)
      setLoading(false)
    } else {
      setSearchText(searchText)
      setCurrentBlogs(blogsData?.blogs)
      setSearchedBlogs({blogs: [], page_number: 1, total_blog: '', total_page: ''})
    }
  }
  const pageCount = React.useMemo(() => {
    let count = 0
    if (searchedBlogs?.total_page && searchText) {
      count = searchedBlogs?.total_page || 0
    } else if (!searchText) {
      count = blogsData?.total_page || 0
    }
    return Array.from({length: count}, (x, i) => i)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, blogsData?.total_page])

  const isNextBtnDisabled = React.useMemo(() => {
    return searchText
      ? searchedBlogs?.total_page == activePage
      : blogsData?.total_page == activePage
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [[searchText, blogsData?.total_page]])

  React.useEffect(() => {
    getBlogs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get('tag')])

  React.useEffect(() => {
    const getTags = async () => {
      try {
        const data = await getWordPressCategories()
        setCategories(data)
      } catch (error) {
        console.error({error})
      }
    }
    getTags()
  }, [])

  const Loader = () => (
    <div className={styles.loader}>
      <div></div>
      <div></div>
      <div></div>
      <div></div>
    </div>
  )

  return (
    <>
      <BlogFilter
        getBlogsBySearch={getBlogsBySearch}
        setSearchedBlogs={setSearchedBlogs}
        setSearchText={setSearchText}
        categories={categories}
      />
      {/* {blogsData?.page_number === 1 && <BlogHeader />}
      {recommendedBlog && searchText === '' && blogsData?.page_number === 1 && (
        <HighlightBlog recommendedBlog={recommendedBlog} />
      )} */}
      <div className={styles.blogs}>
        <div className={styles.container}>
          {currentBlogs?.length === 0 && !loading && (
            <p className={styles.noBlogs}>No blogs found</p>
          )}
          <div className={styles.blogContainer}>
            {
              //   (!searchText && blogsData?.page_number === 1
              //   ? structuredClone(currentBlogs)?.slice(1, 15)
              //   : currentBlogs
              // )
              currentBlogs?.map((blog, index) => (
                <Link href={`/blogs/${blog.slug}`} key={index} className={styles.blog}>
                  <div>
                    <div className={styles.image}>
                      <Image
                        priority
                        src={
                          blog?.jetpack_featured_media_url ||
                          blog?._embedded?.self?.[0]?.jetpack_featured_media_url
                        }
                        alt="blog"
                        width={426}
                        height={240}
                      />
                    </div>
                    <div className={styles.info}>
                      <p className={styles.title}>
                        {parse(
                          blog.title.rendered ||
                            blog?._embedded?.self?.[0]?.title?.rendered ||
                            'No Title',
                        )}
                      </p>
                      <div className={styles.cardFooter}>
                        <div className={styles.authorAndDate}>
                          {/* <p>{blog?._embedded.author ? blog?._embedded.author[0].name : ''}</p> */}
                          <div>
                            {blog?.date || blog?._embedded.self[0]?.date
                              ? format(
                                  new Date(blog?.date || blog?._embedded.self[0]?.date),
                                  'dd MMM, yyyy',
                                )
                              : ''}
                          </div>
                        </div>
                        <div className={styles.tagAndTime}>
                          <div className={styles.tags}>
                            {(
                              (blog?._embedded['wp:term'] && blog?._embedded['wp:term'][0]) ||
                              []
                            ).map((tag: {slug: string; name: string}) =>
                              tag.name !== 'Recommended' ? (
                                <Tag key={tag?.slug} text={tag.name} slug={tag?.slug} />
                              ) : null,
                            )}
                          </div>
                          <div>{`${getBlogReadingTime(
                            blog?.content?.rendered || '4 minutes',
                          )}`}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            }
            {loading && <Loader />}
          </div>
        </div>
        {!!currentBlogs?.length && (
          <div className={styles.paginationContainer}>
            <div
              onClick={() =>
                activePage !== 1
                  ? searchText
                    ? getBlogsBySearch(searchText, activePage - 1)
                    : getBlogs(activePage - 1)
                  : () => {}
              }
              className={clsx(
                styles.previousClassName,
                activePage === 1 ? styles.disabledClassName : '',
              )}
            >{`<`}</div>
            {pageCount.map((page, index) => (
              <div
                key={index}
                onClick={() => (searchText ? getBlogsBySearch(searchText, 1) : getBlogs(index + 1))}
                className={clsx(
                  styles.previousClassName,
                  index + 1 === activePage ? styles.activeClassName : styles.pageLinkClassName,
                )}
              >
                {index + 1}
              </div>
            ))}
            <div
              onClick={() =>
                isNextBtnDisabled
                  ? () => {}
                  : searchText
                  ? getBlogsBySearch(searchText, activePage + 1)
                  : getBlogs(activePage + 1)
              }
              className={clsx(
                styles.nextClassName,
                isNextBtnDisabled ? styles.disabledClassName : '',
              )}
            >{`>`}</div>
          </div>
        )}
      </div>
    </>
  )
}

const BlogsWrapper = () => {
  return (
    <React.Suspense>
      <Blogs />
    </React.Suspense>
  )
}

export default BlogsWrapper
